(() => {
  if (customElements.get('gfe-crystal-guide')) return;

  const guides = new Map();
  const allowedTags = new Set(['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'UL', 'OL', 'LI', 'BLOCKQUOTE', 'H4', 'H5', 'H6', 'A', 'SPAN', 'DIV', 'SUP', 'SUB']);
  const discardedTags = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'LINK', 'META', 'FORM', 'INPUT', 'BUTTON', 'TEXTAREA', 'SELECT', 'SVG', 'MATH', 'IMG', 'VIDEO', 'AUDIO', 'SOURCE']);
  let lastInput = 'pointer';
  let activeGuide = null;

  document.addEventListener('keydown', (event) => {
    if (!event.altKey && !event.ctrlKey && !event.metaKey) lastInput = 'keyboard';
  }, true);
  document.addEventListener('pointerdown', () => { lastInput = 'pointer'; }, { capture: true, passive: true });

  function safeUrl(value) {
    if (!value) return null;
    try {
      const url = new URL(value, document.baseURI);
      return ['https:', 'http:'].includes(url.protocol) ? url.href : null;
    } catch {
      return null;
    }
  }

  function safeFragment(html) {
    const source = document.createElement('template');
    source.innerHTML = String(html || '');
    const output = document.createDocumentFragment();
    const append = (parent, node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        parent.append(document.createTextNode(node.textContent));
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE || discardedTags.has(node.tagName)) return;
      let target = parent;
      if (allowedTags.has(node.tagName)) {
        target = document.createElement(node.tagName.toLowerCase());
        if (node.tagName === 'A') {
          const href = safeUrl(node.getAttribute('href'));
          if (href) target.setAttribute('href', href);
        }
        parent.append(target);
      }
      for (const child of node.childNodes) append(target, child);
    };
    for (const child of source.content.childNodes) append(output, child);
    return output;
  }

  function productFragment(template) {
    const fragment = template.content.cloneNode(true);
    const richTags = new Set('A ABBR ADDRESS ARTICLE ASIDE AUDIO B BLOCKQUOTE BR CAPTION CITE CODE COL COLGROUP DD DEL DFN DIV DL DT EM FIGCAPTION FIGURE H1 H2 H3 H4 H5 H6 HR I IFRAME IMG INS KBD LI MARK OL P PICTURE PRE Q S SAMP SECTION SMALL SOURCE SPAN STRONG SUB SUP TABLE TBODY TD TFOOT TH THEAD TIME TR TRACK U UL VAR VIDEO'.split(' '));
    const blockedTags = new Set(['SCRIPT', 'STYLE', 'OBJECT', 'EMBED', 'BASE', 'LINK', 'META', 'TEMPLATE', 'SVG', 'MATH', 'INPUT', 'BUTTON', 'TEXTAREA', 'SELECT']);
    const urlAttributes = new Set(['href', 'src', 'poster', 'cite', 'background', 'longdesc', 'action', 'formaction', 'xlink:href']);
    const safeProductUrl = (value, attribute, tag) => {
      const normalized = value.replace(/[\u0000-\u0020\u007f-\u009f]/g, '');
      if ((tag === 'IMG' || attribute === 'poster') && /^data:image\/(?:png|jpe?g|gif|webp|avif|bmp);base64,[a-z0-9+/=]+$/i.test(normalized)) return true;
      try {
        const protocol = new URL(normalized, document.baseURI).protocol;
        return ['https:', 'http:'].includes(protocol) || (tag === 'A' && attribute === 'href' && ['mailto:', 'tel:'].includes(protocol));
      } catch {
        return false;
      }
    };
    for (const node of [...fragment.querySelectorAll('*')]) {
      if (blockedTags.has(node.tagName)) {
        node.remove();
        continue;
      }
      if (!richTags.has(node.tagName)) {
        node.replaceWith(...node.childNodes);
        continue;
      }
      for (const attribute of [...node.attributes]) {
        const name = attribute.name.toLowerCase();
        let unsafe = name.startsWith('on') || ['srcdoc', 'is', 'ping'].includes(name);
        if (urlAttributes.has(name)) unsafe ||= !safeProductUrl(attribute.value, name, node.tagName);
        if (name === 'srcset') unsafe ||= !attribute.value.split(',').every((candidate) => safeProductUrl(candidate.trim().split(/\s+/)[0], 'src', node.tagName));
        if (name === 'style') {
          const decoded = attribute.value.replace(/\/\*[\s\S]*?\*\//g, '').replace(/\\([0-9a-f]{1,6})\s?/gi, (_, hex) => String.fromCodePoint(Math.min(parseInt(hex, 16), 0x10ffff))).replace(/[\u0000-\u0020\u007f-\u009f]/g, '');
          unsafe ||= /expression\(|(?:javascript|vbscript):|behavior:|-moz-binding/i.test(decoded);
        }
        if (unsafe) node.removeAttribute(attribute.name);
      }
    }
    return fragment;
  }

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = String(text);
    return node;
  }

  function appendCopy(parent, html) {
    if (!html) return;
    const copy = element('div', 'gfe-guide-copy');
    copy.append(safeFragment(html));
    parent.append(copy);
  }

  function appendCaption(parent, caption) {
    if (!caption) return;
    const node = element('p', 'gfe-guide-caption');
    node.append(safeFragment(caption));
    parent.append(node);
  }

  function appendTitle(parent, source, tag = 'h4') {
    const value = source.titleHtml || source.title;
    if (!value || !String(value).trim()) return null;
    const node = element(tag);
    node.append(safeFragment(value));
    parent.append(node);
    return node;
  }

  function lockPage() {
    const body = document.body;
    const root = document.documentElement;
    const x = window.scrollX;
    const y = window.scrollY;
    const keys = ['position', 'top', 'left', 'right', 'width', 'overflow', 'paddingRight'];
    const saved = Object.fromEntries(keys.map((key) => [key, body.style[key]]));
    const rootOverflow = root.style.overflow;
    const scrollbar = window.innerWidth - root.clientWidth;
    const padding = parseFloat(getComputedStyle(body).paddingRight) || 0;
    Object.assign(body.style, { position: 'fixed', top: `-${y}px`, left: '0', right: '0', width: '100%', overflow: 'hidden', paddingRight: `${padding + scrollbar}px` });
    root.style.overflow = 'hidden';
    return () => {
      Object.assign(body.style, saved);
      root.style.overflow = rootOverflow;
      const behavior = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      window.scrollTo(x, y);
      root.style.scrollBehavior = behavior;
    };
  }

  function fetchGuide(url) {
    if (!guides.has(url)) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      const request = fetch(url, { signal: controller.signal, credentials: 'omit' })
        .then((response) => {
          if (!response.ok) throw new Error('Guide unavailable');
          return response.json();
        })
        .catch((error) => { guides.delete(url); throw error; })
        .finally(() => clearTimeout(timeout));
      guides.set(url, request);
    }
    return guides.get(url);
  }

  class GfeCrystalGuide extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.entry = this.querySelector('[data-guide-open]');
      this.dialog = this.querySelector('[data-guide-dialog]');
      this.titleElement = this.querySelector('[data-guide-title]');
      this.scroll = this.querySelector('[data-guide-scroll]');
      this.article = this.querySelector('[data-guide-document]');
      if (!this.entry || !this.dialog || !this.article || !this.scroll) return;
      this.events = new AbortController();
      this.state = 'closed';
      this.sequence = 0;
      this.loadSequence = 0;
      this.frames = [];
      const options = { signal: this.events.signal };
      this.entryInView = false;
      this.updateEntryVisibility();
      document.addEventListener('visibilitychange', () => this.updateEntryVisibility(), options);
      if (typeof IntersectionObserver === 'function') {
        const observer = new IntersectionObserver((entries) => {
          if (this.entryObserver !== observer) return;
          const entry = entries.find((item) => item.target === this.entry);
          if (!entry) return;
          this.entryInView = entry.isIntersecting && entry.intersectionRatio > 0;
          this.updateEntryVisibility();
        }, { threshold: [0, 0.001] });
        this.entryObserver = observer;
        observer.observe(this.entry);
      }
      this.entry.addEventListener('click', (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.open();
      }, options);
      this.entry.addEventListener('keydown', (event) => {
        if (event.key === ' ') { event.preventDefault(); this.open(); }
      }, options);
      this.querySelectorAll('[data-guide-close]').forEach((button) => button.addEventListener('click', () => this.close(), options));
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close({ immediate: true });
      }, options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      }, options);
      this.dialog.addEventListener('keydown', (event) => this.trapFocus(event), { ...options, capture: true });
      this.dialog.addEventListener('close', () => {
        if (!this.dialog.open && this.state !== 'closed') this.finishClose();
      }, options);
    }

    disconnectedCallback() {
      this.entryObserver?.disconnect();
      this.entryObserver = null;
      this.entryInView = false;
      this.updateEntryVisibility();
      this.close({ immediate: true, restoreFocus: false });
      this.events?.abort();
      this.events = null;
      this.loadSequence += 1;
      this.loading = false;
    }

    updateEntryVisibility() {
      this.dataset.entryVisible = String(this.isConnected && this.entryInView && !document.hidden);
    }

    clearTransition() {
      clearTimeout(this.closeTimer);
      this.frames?.forEach((frame) => cancelAnimationFrame(frame));
      this.frames = [];
      this.sequence += 1;
    }

    open() {
      if (!this.dialog || typeof this.dialog.showModal !== 'function') {
        const fallback = safeUrl(this.dataset.guideFallbackUrl);
        if (fallback) window.location.assign(fallback);
        return;
      }
      if (activeGuide && activeGuide !== this) activeGuide.close({ immediate: true, restoreFocus: false });
      this.clearTransition();
      const sequence = this.sequence;
      const alreadyOpen = this.dialog.open;
      this.state = 'open';
      activeGuide = this;
      this.dataset.interacted = 'true';
      this.entry.setAttribute('aria-expanded', 'true');
      if (!alreadyOpen) {
        this.opener = this.entry;
        this.noMotion = lastInput === 'keyboard' || matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.dialog.dataset.motion = this.noMotion ? 'none' : 'full';
        this.unlock = lockPage();
        this.dialog.showModal();
        this.scroll.scrollTop = 0;
      }
      if (this.noMotion || alreadyOpen) {
        this.dialog.dataset.visible = 'true';
      } else {
        this.frames.push(requestAnimationFrame(() => {
          this.frames.push(requestAnimationFrame(() => {
            if (this.sequence === sequence && this.state === 'open') this.dialog.dataset.visible = 'true';
          }));
        }));
      }
      if (!this.loaded && !this.loading) this.load();
    }

    close({ immediate = false, restoreFocus = true } = {}) {
      if (!this.dialog || this.state === 'closed') return;
      this.clearTransition();
      const sequence = this.sequence;
      this.state = 'closing';
      this.dialog.dataset.visible = 'false';
      this.entry.setAttribute('aria-expanded', 'false');
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (immediate || reduced || this.noMotion) {
        this.dialog.dataset.motion = 'none';
        this.finishClose(restoreFocus);
      } else {
        this.closeTimer = setTimeout(() => {
          if (this.sequence === sequence && this.state === 'closing') this.finishClose(restoreFocus);
        }, 260);
      }
    }

    finishClose(restoreFocus = true) {
      this.state = 'closed';
      this.dialog.dataset.visible = 'false';
      this.entry.setAttribute('aria-expanded', 'false');
      if (this.dialog.open) this.dialog.close();
      this.unlock?.();
      this.unlock = null;
      if (activeGuide === this) activeGuide = null;
      if (restoreFocus && this.opener?.isConnected) this.opener.focus({ preventScroll: true });
    }

    trapFocus(event) {
      if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return;
      const nodes = [...this.dialog.querySelectorAll('a[href], button, input, select, textarea, [tabindex], [contenteditable="true"]')]
        .filter((node) => node.tabIndex >= 0 && !node.matches(':disabled, [aria-disabled="true"]') && !node.closest('[hidden], [inert], [aria-hidden="true"]') && node.getClientRects().length > 0 && getComputedStyle(node).visibility === 'visible');
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = this.ownerDocument.activeElement;
      if (!first) { event.preventDefault(); this.dialog.focus(); return; }
      if (event.shiftKey && (active === first || !this.dialog.contains(active))) {
        event.preventDefault(); last.focus({ preventScroll: true });
      } else if (!event.shiftKey && (active === last || !this.dialog.contains(active))) {
        event.preventDefault(); first.focus({ preventScroll: true });
      }
    }

    async load() {
      if (this.hasAttribute('data-guide-references') || this.querySelector('template[data-guide-product-notes]')) {
        return this.loadProductGuides();
      }
      const sequence = ++this.loadSequence;
      this.loading = true;
      this.article.setAttribute('aria-busy', 'true');
      if (this.article.contains(this.ownerDocument.activeElement)) this.scroll.focus({ preventScroll: true });
      const status = element('p', 'gfe-guide-status', this.dataset.guideLoading);
      status.setAttribute('role', 'status');
      this.article.replaceChildren(status);
      try {
        const url = safeUrl(this.dataset.guideSource);
        if (!url) throw new Error('Guide URL missing');
        const all = await fetchGuide(url);
        const locale = (this.dataset.guideLocale || '').startsWith('en') ? 'en' : 'zh';
        const guide = all[locale];
        if (!guide || !Array.isArray(guide.sections) || !guide.sections.length) throw new Error('Guide content missing');
        if (!this.isConnected || this.loadSequence !== sequence) return;
        this.renderGuide(guide);
        this.loaded = true;
      } catch {
        if (!this.isConnected || this.loadSequence !== sequence) return;
        this.renderError();
      } finally {
        if (this.loadSequence === sequence) {
          this.loading = false;
          this.article.removeAttribute('aria-busy');
        }
      }
    }

    productNotes() {
      const template = this.querySelector('template[data-guide-product-notes]');
      if (!template) return null;
      const copy = element('div', 'gfe-guide-copy');
      copy.append(productFragment(template));
      if (!copy.textContent.trim() && !copy.querySelector('img, picture, video, audio, iframe')) return null;
      const notes = element('section', 'gfe-guide-product-notes');
      if (this.dataset.guideNotesTitle) {
        const title = element('h3', 'gfe-guide-group-heading', this.dataset.guideNotesTitle);
        title.id = `${this.dialog.id}-product-notes`;
        notes.setAttribute('aria-labelledby', title.id);
        notes.append(title);
      }
      notes.append(copy);
      return notes;
    }

    async loadProductGuides() {
      const sequence = ++this.loadSequence;
      this.loaded = false;
      if (this.article.contains(this.ownerDocument.activeElement)) this.scroll.focus({ preventScroll: true });
      let references;
      try {
        references = JSON.parse(this.dataset.guideReferences || '[]');
        if (!Array.isArray(references)) throw new Error('Guide references missing');
      } catch {
        const error = element('section', 'gfe-guide-group');
        this.renderProductError(error, {}, () => this.load());
        this.article.replaceChildren(error);
        const notes = this.productNotes();
        if (notes) this.article.append(notes);
        this.loading = false;
        this.article.removeAttribute('aria-busy');
        return;
      }
      this.productRecords = references.map((value, index) => {
        const reference = value && typeof value === 'object' ? value : {};
        const group = element('section', 'gfe-guide-group');
        const title = element('h3', 'gfe-guide-group-heading', reference.title || this.titleElement.textContent);
        title.id = `${this.dialog.id}-guide-${index + 1}`;
        group.setAttribute('aria-labelledby', title.id);
        group.append(title);
        const content = element('div', 'gfe-guide-group-content');
        group.append(content);
        return { reference, group, title, content, index, status: 'pending' };
      });
      this.article.replaceChildren(...this.productRecords.map((record) => record.group));
      const notes = this.productNotes();
      if (notes) this.article.append(notes);
      await Promise.all(this.productRecords.map((record) => this.loadProductReference(record, sequence)));
      if (!this.isConnected || this.loadSequence !== sequence) return;
      this.updateProductBusy();
    }

    updateProductBusy() {
      this.loading = this.productRecords.some((record) => record.status === 'loading' || record.status === 'pending');
      this.loaded = !this.loading;
      if (this.loading) this.article.setAttribute('aria-busy', 'true');
      else this.article.removeAttribute('aria-busy');
    }

    async loadProductReference(record, sequence) {
      if (record.status === 'loading') return;
      if (record.content.contains(this.ownerDocument.activeElement)) this.scroll.focus({ preventScroll: true });
      record.status = 'loading';
      record.content.setAttribute('aria-busy', 'true');
      const status = element('p', 'gfe-guide-status', this.dataset.guideLoading);
      status.setAttribute('role', 'status');
      record.content.replaceChildren(status);
      this.updateProductBusy();
      const url = safeUrl(record.reference.source);
      try {
        if (!url) throw new Error('Guide URL missing');
        const all = await fetchGuide(url);
        const locale = (this.dataset.guideLocale || '').startsWith('en') ? 'en' : 'zh';
        const guide = all?.[locale];
        if (!guide || !Array.isArray(guide.sections) || !guide.sections.length) {
          guides.delete(url);
          throw new Error('Guide content missing');
        }
        if (!this.isConnected || this.loadSequence !== sequence) return;
        if (!record.reference.title && guide.title) record.title.textContent = String(guide.title);
        this.renderGuide(guide, {
          target: record.content,
          imageUrl: record.reference.image || '',
          idPrefix: `${this.dialog.id}-guide-${record.index + 1}`,
          updateTitle: false,
          sectionHeading: 'h4',
          detailHeading: 'h5'
        });
        if (guide.title && String(guide.title) !== record.title.textContent) {
          record.content.prepend(element('p', 'gfe-guide-source-title', guide.title));
        }
        record.status = 'success';
      } catch {
        if (!this.isConnected || this.loadSequence !== sequence) return;
        record.status = 'error';
        this.renderProductError(record.content, record.reference, () => this.loadProductReference(record, sequence));
      } finally {
        if (this.loadSequence === sequence) {
          record.content.removeAttribute('aria-busy');
          this.updateProductBusy();
        }
      }
    }

    renderProductError(target, reference, retryLoad) {
      const message = element('p', 'gfe-guide-status', this.dataset.guideError);
      message.setAttribute('role', 'alert');
      const retry = element('button', 'gfe-guide-retry', this.dataset.guideRetry);
      retry.type = 'button';
      retry.addEventListener('click', retryLoad, { once: true });
      target.replaceChildren(message, retry);
      const fallback = safeUrl(reference.fallbackUrl);
      if (fallback) {
        const link = element('a', 'gfe-guide-fallback', this.dataset.guideFallbackLabel);
        link.href = fallback;
        target.append(link);
      }
    }

    renderGuide(guide, { target = this.article, imageUrl = this.dataset.guideImage, idPrefix = this.dialog.id, updateTitle = true, sectionHeading = 'h3', detailHeading = 'h4' } = {}) {
      const fragment = document.createDocumentFragment();
      if (updateTitle) this.titleElement.textContent = String(guide.title || this.titleElement.textContent);
      const photo = safeUrl(imageUrl);
      if (photo) {
        const figure = element('figure', 'gfe-guide-figure');
        const image = element('img');
        image.src = photo;
        image.alt = String(guide.title || '');
        image.decoding = 'async';
        figure.append(image);
        fragment.append(figure);
      }
      if (guide.subtitle) fragment.append(element('p', 'gfe-guide-subtitle', guide.subtitle));
      guide.sections.forEach((source, index) => {
        const section = element('section', 'gfe-guide-section');
        let hasHeading = false;
        const appendSectionHeading = (headingSource) => {
          const heading = element('div', 'gfe-guide-section-heading');
          const title = appendTitle(heading, headingSource, sectionHeading);
          if (!title) return;
          const number = element('span', 'gfe-guide-section-number', String(index + 1).padStart(2, '0'));
          number.setAttribute('aria-hidden', 'true');
          heading.prepend(number);
          title.id = `${idPrefix}-section-${index + 1}`;
          section.setAttribute('aria-labelledby', title.id);
          section.append(heading);
          hasHeading = true;
        };
        const blocks = Array.isArray(source.blocks) ? source.blocks : [];
        if (blocks.some((block) => block.titleHtml || block.title || block.bodyHtml || block.caption)) {
          blocks.forEach((block) => {
            appendCaption(section, block.caption);
            if (hasHeading) appendTitle(section, block, detailHeading);
            else appendSectionHeading(block);
            appendCopy(section, block.bodyHtml);
          });
        } else {
          appendCaption(section, source.caption);
          appendSectionHeading(source);
          appendCopy(section, source.bodyHtml || source.text);
        }
        if (Array.isArray(source.items) && source.items.length) {
          const list = element('ol', 'gfe-guide-items');
          source.items.forEach((item, itemIndex) => {
            const row = element('li');
            const ordinal = element('span', 'gfe-guide-item-number', String(itemIndex + 1).padStart(2, '0'));
            ordinal.setAttribute('aria-hidden', 'true');
            const detail = element('div');
            appendCaption(detail, item.caption);
            appendTitle(detail, item, detailHeading);
            appendCopy(detail, item.bodyHtml);
            row.append(ordinal, detail);
            list.append(row);
          });
          section.append(list);
        }
        fragment.append(section);
      });
      target.replaceChildren(fragment);
    }

    renderError() {
      const message = element('p', 'gfe-guide-status', this.dataset.guideError);
      message.setAttribute('role', 'alert');
      const retry = element('button', 'gfe-guide-retry', this.dataset.guideRetry);
      retry.type = 'button';
      retry.addEventListener('click', () => this.load(), { once: true });
      this.article.replaceChildren(message, retry);
      const fallback = safeUrl(this.dataset.guideFallbackUrl);
      if (fallback) {
        const link = element('a', 'gfe-guide-fallback', this.dataset.guideFallbackLabel);
        link.href = fallback;
        this.article.append(link);
      }
    }
  }

  customElements.define('gfe-crystal-guide', GfeCrystalGuide);
})();
